// Booking filter persistence utility

import { eraseCookieData, getCookieData, setCookieData } from "../helpers/cookie";

// localStorage keys for booking filters
export const BOOKING_STORAGE_KEYS = {
  SAVED_FILTER: 'bookings_savedFilter',
  SIGNEE_FILTER: 'bookings_signeeFilter',
  DISPLAY_PRESET: 'bookings_displayPreset',
  SHOW_FILTERS: 'bookings_showFilters',
  SHOW_ADVANCE_FILTER: 'bookings_showAdvanceFilter',
  INNER_TABLE_FILTERS: 'bookings_innerTableFilters',
  SEARCH_QUERY: 'bookings_searchQuery',
  SELECTED_DATE_RANGE: 'bookings_selectedDateRange',
  VIEW: 'bookings_view',
  IS_EQUIPMENT_VIEW: 'bookings_isEquipmentView',
  LIST_GROUP_BY_VIEW: 'bookings_listGroupByView',
  TIMELINE_GROUP_BY_VIEW: "booking_timelineGroupByView",
  TIMELINE_TIMEFRAME_VIEW: "booking_timelineTimeframeView"
};

export const MY_BOOKING_STORAGE_KEYS = {
  SAVED_FILTER: 'my_bookings_savedFilter',
  SIGNEE_FILTER: 'my_bookings_signeeFilter',
  DISPLAY_PRESET: 'my_bookings_displayPreset',
  SHOW_FILTERS: 'my_bookings_showFilters',
  SHOW_ADVANCE_FILTER: 'my_bookings_showAdvanceFilter',
  INNER_TABLE_FILTERS: 'my_bookings_innerTableFilters',
  SEARCH_QUERY: 'my_bookings_searchQuery',
  SELECTED_DATE_RANGE: 'my_bookings_selectedDateRange',
  VIEW: 'my_bookings_view',
  IS_EQUIPMENT_VIEW: 'my_bookings_isEquipmentView',
  LIST_GROUP_BY_VIEW: 'my_bookings_listGroupByView',
  TIMELINE_GROUP_BY_VIEW: "my_bookings_timelineGroupByView",
  TIMELINE_TIMEFRAME_VIEW: "my_bookings_timelineTimeframeView"
};

export const GROUP_BOOKING_STORAGE_KEYS = {
  SAVED_FILTER: 'bookings_group_savedFilter',
  SIGNEE_FILTER: 'bookings_group_signeeFilter',
  DISPLAY_PRESET: 'bookings_group_displayPreset',
  SHOW_FILTERS: 'bookings_group_showFilters',
  SHOW_ADVANCE_FILTER: 'bookings_group_showAdvanceFilter',
  INNER_TABLE_FILTERS: 'bookings_group_innerTableFilters',
  SEARCH_QUERY: 'bookings_group_searchQuery',
  SELECTED_DATE_RANGE: 'bookings_group_selectedDateRange',
  VIEW: 'bookings_group_view',
  IS_EQUIPMENT_VIEW: 'bookings_group_isEquipmentView',
  LIST_GROUP_BY_VIEW: 'bookings_group_listGroupByView',
  TIMELINE_GROUP_BY_VIEW: 'bookings_group_timelineGroupByView',
  TIMELINE_TIMEFRAME_VIEW: 'bookings_group_timelineTimeframeView'
};

export const BOOK_EQUIPMENT_STORAGE_KEYS = {
  TIMELINE_GROUP_BY_VIEW: 'book_equipment_timelineGroupByView',
  TIMELINE_TIMEFRAME_VIEW: 'book_equipment_timelineTimeframeView'
};

export const RESET_ALL_LOGIN_SESSION_MARKER = "reset_all_login_cleanup_done";

let shouldResetBookEquipmentOnNextLoad = false;
let bookEquipmentSessionPreferences = null;

const RESET_ALL_RELEVANT_STORAGE_KEYS = [
  BOOKING_STORAGE_KEYS.SAVED_FILTER,
  BOOKING_STORAGE_KEYS.SIGNEE_FILTER,
  BOOKING_STORAGE_KEYS.DISPLAY_PRESET,
  BOOKING_STORAGE_KEYS.INNER_TABLE_FILTERS,
  BOOKING_STORAGE_KEYS.SEARCH_QUERY,
  BOOKING_STORAGE_KEYS.SELECTED_DATE_RANGE,
  MY_BOOKING_STORAGE_KEYS.SAVED_FILTER,
  MY_BOOKING_STORAGE_KEYS.SIGNEE_FILTER,
  MY_BOOKING_STORAGE_KEYS.DISPLAY_PRESET,
  MY_BOOKING_STORAGE_KEYS.INNER_TABLE_FILTERS,
  MY_BOOKING_STORAGE_KEYS.SEARCH_QUERY,
  MY_BOOKING_STORAGE_KEYS.SELECTED_DATE_RANGE,
  GROUP_BOOKING_STORAGE_KEYS.SAVED_FILTER,
  GROUP_BOOKING_STORAGE_KEYS.SIGNEE_FILTER,
  GROUP_BOOKING_STORAGE_KEYS.DISPLAY_PRESET,
  GROUP_BOOKING_STORAGE_KEYS.INNER_TABLE_FILTERS,
  GROUP_BOOKING_STORAGE_KEYS.SEARCH_QUERY,
  GROUP_BOOKING_STORAGE_KEYS.SELECTED_DATE_RANGE,
];

const COOKIE_TTL_DAYS = 7;
const MAX_COOKIE_VALUE_LENGTH = 3500;
const LOCAL_STORAGE_MARKER = "__LOCAL_STORAGE__";

const getScopedStorageKey = (key, options = {}) => {
  const namespace = options?.namespace;
  if (!namespace) return key;
  return `${namespace}__${key}`;
};

const parseStoredValue = (saved, defaultValue) => {
  if (saved === null || saved === undefined) return defaultValue;
  try {
    return JSON.parse(saved);
  } catch {
    return saved;
  }
};

const writeLocalStorageValue = (key, serializedValue) => {
  try {
    localStorage.setItem(key, serializedValue);
  } catch (error) {
    console.warn(`Failed to write localStorage for key: ${key}`, error);
  }
};

const readLocalStorageValue = (key, defaultValue) => {
  try {
    const saved = localStorage.getItem(key);
    if (saved === null) return defaultValue;
    return parseStoredValue(saved, defaultValue);
  } catch (error) {
    console.warn(`Failed to read localStorage for key: ${key}`, error);
    return defaultValue;
  }
};

const persistToScopedCookie = (scopedKey, value) => {
  const serializedValue = typeof value === 'object' ? JSON.stringify(value) : value;
  writeLocalStorageValue(scopedKey, String(serializedValue));

  // Cookie size limits are strict; keep large payloads in localStorage with a marker cookie.
  const encodedValue = encodeURIComponent(String(serializedValue));
  if (encodedValue.length > MAX_COOKIE_VALUE_LENGTH) {
    setCookieData(scopedKey, encodeURIComponent(LOCAL_STORAGE_MARKER), COOKIE_TTL_DAYS);
    return;
  }

  const valueToStore = encodeURIComponent(String(serializedValue));
  setCookieData(scopedKey, valueToStore, COOKIE_TTL_DAYS);

  const persistedCookieValue = getCookieData(scopedKey);
  if (persistedCookieValue !== valueToStore) {
    setCookieData(scopedKey, encodeURIComponent(LOCAL_STORAGE_MARKER), COOKIE_TTL_DAYS);
  }
};

const hasStoredCookieValue = (value) => value !== null && value !== undefined;

const decodeCookieValue = (cookieValue) => {
  if (!hasStoredCookieValue(cookieValue)) return null;
  return decodeURIComponent(cookieValue);
};

const readFromDecodedCookie = (decodedCookieValue, scopedKey, defaultValue) => {
  if (decodedCookieValue === LOCAL_STORAGE_MARKER) {
    return readLocalStorageValue(scopedKey, defaultValue);
  }
  return parseStoredValue(decodedCookieValue, defaultValue);
};

const migrateLocalStorageValue = (scopedKey, localStorageValue) => {
  if (localStorageValue === null) return null;
  persistToScopedCookie(scopedKey, localStorageValue);
  return localStorageValue;
};

const readLegacyMarkerValue = (scopedKey, legacyKey, defaultValue) => {
  const scopedSaved = readLocalStorageValue(scopedKey, null);
  if (scopedSaved !== null) return scopedSaved;
  return readLocalStorageValue(legacyKey, defaultValue);
};

const removePersistedStorageEntry = (storageKey) => {
  try {
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.warn(`Failed to remove localStorage key: ${storageKey}`, error);
  }

  try {
    eraseCookieData(storageKey);
  } catch (error) {
    console.warn(`Failed to remove cookie key: ${storageKey}`, error);
  }
};

const shouldClearResetAllStorageKey = (storageKey) => {
  return RESET_ALL_RELEVANT_STORAGE_KEYS.some((targetKey) => {
    return storageKey === targetKey || storageKey.endsWith(`__${targetKey}`);
  });
};

// Get a filter value from localStorage
export const getPersistedFilter = (key, defaultValue = null, options = {}) => {
  try {
    const scopedKey = getScopedStorageKey(key, options);

    const decodedCookieValue = decodeCookieValue(getCookieData(scopedKey));
    if (decodedCookieValue !== null) {
      return readFromDecodedCookie(decodedCookieValue, scopedKey, defaultValue);
    }

    const decodedLegacyCookieValue = decodeCookieValue(getCookieData(key));
    if (decodedLegacyCookieValue !== null) {
      if (decodedLegacyCookieValue === LOCAL_STORAGE_MARKER) {
        return readLegacyMarkerValue(scopedKey, key, defaultValue);
      }

      const parsed = parseStoredValue(decodedLegacyCookieValue, defaultValue);
      persistToScopedCookie(scopedKey, parsed);
      return parsed;
    }

    const migratedScopedLocalStorageValue = migrateLocalStorageValue(
      scopedKey,
      readLocalStorageValue(scopedKey, null),
    );
    if (migratedScopedLocalStorageValue !== null) return migratedScopedLocalStorageValue;

    const migratedLegacyLocalStorageValue = migrateLocalStorageValue(
      scopedKey,
      readLocalStorageValue(key, null),
    );
    if (migratedLegacyLocalStorageValue !== null) return migratedLegacyLocalStorageValue;

    return defaultValue;
  } catch (error) {
    console.warn(`Failed to get persisted filter for key: ${key}`, error);
    return defaultValue;
  }
};

// Set a filter value in localStorage
export const setPersistedFilter = (key, value, options = {}) => {
  try {
    const scopedKey = getScopedStorageKey(key, options);
    persistToScopedCookie(scopedKey, value);
  } catch (error) {
    console.warn(`Failed to persist filter for key: ${key}`, error);
  }
};

export const clearResetAllStartupPreferences = () => {
  try {
    Object.keys(localStorage).forEach((storageKey) => {
      if (shouldClearResetAllStorageKey(storageKey)) {
        removePersistedStorageEntry(storageKey);
      }
    });
  } catch (error) {
    console.warn("Failed to clear Reset All startup preferences from localStorage", error);
  }

  try {
    const cookiePairs = document.cookie ? document.cookie.split(";") : [];
    cookiePairs.forEach((pair) => {
      const cookieKey = pair.split("=")[0]?.trim();
      if (cookieKey && shouldClearResetAllStorageKey(cookieKey)) {
        removePersistedStorageEntry(cookieKey);
      }
    });
  } catch (error) {
    console.warn("Failed to clear Reset All startup preferences from cookies", error);
  }
};

export const markBookEquipmentStartupResetPending = () => {
  shouldResetBookEquipmentOnNextLoad = true;
};

export const consumeBookEquipmentStartupReset = () => {
  if (!shouldResetBookEquipmentOnNextLoad) {
    return false;
  }

  shouldResetBookEquipmentOnNextLoad = false;
  return true;
};

export const setBookEquipmentSessionPreferences = (preferences) => {
  bookEquipmentSessionPreferences = preferences;
};

export const getBookEquipmentSessionPreferences = () => {
  return bookEquipmentSessionPreferences;
};

// Clear all booking filter preferences
export const clearAllBookingFilterPreferences = () => {
  Object.values(BOOKING_STORAGE_KEYS).forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn(`Failed to remove filter for key: ${key}`, error);
    }
  });
};

// Get all persisted booking filters
export const getAllPersistedBookingFilters = () => {
  const filters = {};
  Object.entries(BOOKING_STORAGE_KEYS).forEach(([name, key]) => {
    filters[name] = getPersistedFilter(key);
  });
  return filters;
};
